import { useState, useEffect, useCallback, useRef } from 'react';
import Modal from '../common/Modal';
import Alert from '../common/Alert';
import ConfirmModal from '../common/ConfirmModal';
import MobileSelect from '../common/MobileSelect';
import { useToast } from '../../context/ToastContext';
import { useConfig } from '../../context/ConfigContext';
import { useCreateAccount, useUpdateAccount, useResetAccountBalance } from '../../hooks/useQueries';
import IconPicker from '../common/IconPicker';
import ColorPicker from '../common/ColorPicker';
import CalculatorInput from '../common/CalculatorInput';
import { accountAPI } from '../../services/api';
import { Info, IndianRupee, Building2, Wallet, Banknote, CreditCard, MoreHorizontal } from 'lucide-react';

const ACCOUNT_TYPES = [
  { value: 'bank', label: 'Bank', icon: Building2, iconColor: '#3b82f6' },
  { value: 'wallet', label: 'Wallet', icon: Wallet, iconColor: '#8b5cf6' },
  { value: 'cash', label: 'Cash', icon: Banknote, iconColor: '#22c55e' },
  { value: 'credit_card', label: 'Cards', icon: CreditCard, iconColor: '#f59e0b' },
  { value: 'other', label: 'Other', icon: MoreHorizontal, iconColor: '#6b7280' }
];

const initialFormData = {
  name: '',
  type: 'bank',
  description: '',
  icon: 'building-2',
  color: '#6366f1',
  balance: 0
};

export default function AddAccountModal({
  isOpen,
  onClose,
  onSuccess,
  account = null
}) {
  const { toast } = useToast();
  const { formatAmount, config } = useConfig();
  const createAccountMutation = useCreateAccount();
  const updateAccountMutation = useUpdateAccount();
  const resetBalanceMutation = useResetAccountBalance();

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [balanceResetConfirm, setBalanceResetConfirm] = useState({ open: false, newBalance: 0 });
  const [deletedAccountInfo, setDeletedAccountInfo] = useState(null);
  const [checkingDeleted, setCheckingDeleted] = useState(false);
  const debounceRef = useRef(null);

  const isEditing = account && account._id;

  // Check for deleted account with the same name (debounced)
  const checkDeletedAccount = useCallback(async (name) => {
    if (!name || name.trim().length < 2 || isEditing) {
      setDeletedAccountInfo(null);
      return;
    }

    setCheckingDeleted(true);
    try {
      const { data } = await accountAPI.checkDeleted(name.trim());
      if (data.data.found) {
        setDeletedAccountInfo(data.data.account);
        // Auto-fill the balance with the calculated balance from deleted account
        setFormData(prev => ({
          ...prev,
          balance: data.data.account.calculatedBalance
        }));
      } else {
        setDeletedAccountInfo(null);
      }
    } catch {
      // Silently fail - just don't show the info
      setDeletedAccountInfo(null);
    } finally {
      setCheckingDeleted(false);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditing) {
      // Use currentBalance (calculated from transactions) for editing, fallback to balance
      const rawBalance = account.currentBalance ?? account.balance ?? 0;
      const balanceToShow = Math.round(rawBalance * 100) / 100;
      setFormData({
        name: account.name,
        type: account.type,
        description: account.description || '',
        icon: account.icon || 'building-2',
        color: account.color || '#6366f1',
        balance: balanceToShow
      });
    } else {
      setFormData(initialFormData);
    }
    setValidationErrors({});
    setDeletedAccountInfo(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, account?._id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }

    // Check for deleted account when name changes (debounced)
    if (name === 'name' && !isEditing) {
      // Clear any pending debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      // Set new debounce
      debounceRef.current = setTimeout(() => {
        checkDeletedAccount(value);
      }, 500);
    }
  };

  const validate = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Account name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      errors.name = 'Name cannot exceed 100 characters';
    }

    if (formData.description && formData.description.length > 200) {
      errors.description = 'Description cannot exceed 200 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const newBalance = formData.balance ? parseFloat(formData.balance) : 0;

    // Check if balance changed during edit (compare with currentBalance if available)
    if (isEditing && account.currentBalance !== undefined) {
      const currentBalance = account.currentBalance;
      if (newBalance !== currentBalance) {
        // Show confirmation dialog
        setBalanceResetConfirm({ open: true, newBalance, currentBalance });
        return;
      }
    }

    await saveAccount();
  };

  const saveAccount = async () => {
    setLoading(true);
    setError(null);

    // Prepare data with balance as number
    const balanceValue = formData.balance === '' || formData.balance === null || formData.balance === undefined
      ? 0
      : parseFloat(formData.balance);

    const submitData = {
      ...formData,
      balance: isNaN(balanceValue) ? 0 : balanceValue
    };

    try {
      let resultAccount;
      if (isEditing) {
        resultAccount = await updateAccountMutation.mutateAsync({ id: account._id, data: submitData });
        toast.success('Account updated successfully');
      } else {
        const result = await createAccountMutation.mutateAsync(submitData);
        resultAccount = result.account || result;
        if (result.restored) {
          if (result.adjustmentTransaction) {
            toast.success(`"${result.account.name}" restored with ${result.restoredTransactions} transaction(s) and balance adjusted`);
          } else {
            toast.success(`"${result.account.name}" restored with ${result.restoredTransactions} transaction(s)`);
          }
        } else {
          toast.success(`"${result.account?.name || submitData.name}" created successfully`);
        }
      }

      // Call onSuccess with the new/updated account if provided
      if (onSuccess) {
        onSuccess(resultAccount || submitData);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save account');
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceResetConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      // Reset balance creates an adjustment transaction
      await resetBalanceMutation.mutateAsync({
        id: account._id,
        newBalance: balanceResetConfirm.newBalance
      });

      // Also update other account fields
      const submitData = {
        ...formData,
        balance: account.balance // Keep original opening balance
      };
      await updateAccountMutation.mutateAsync({ id: account._id, data: submitData });

      toast.success('Balance adjusted successfully');
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset balance');
    } finally {
      setLoading(false);
      setBalanceResetConfirm({ open: false, newBalance: 0, currentBalance: 0 });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Account' : 'Add Account'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert
            variant="error"
            message={error}
            onClose={() => setError(null)}
          />
        )}

        {/* Account Name */}
        <div>
          <label className="label">Account Name</label>
          <div className="relative">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., HDFC Bank, PayPal, Cash"
              enterKeyHint="next"
              className={`input ${validationErrors.name ? 'input-error' : ''}`}
            />
            {checkingDeleted && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-surface-300 border-t-primary-500 rounded-full animate-spin" />
              </div>
            )}
          </div>
          {validationErrors.name && (
            <p className="text-expense-600 text-sm mt-1">
              {validationErrors.name}
            </p>
          )}
          {deletedAccountInfo && !isEditing && (
            <div className="flex items-start gap-2 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Found previously deleted account!</p>
                <p className="mt-1">
                  We found {deletedAccountInfo.transactionCount} transaction(s) with a balance of{' '}
                  <strong>{formatAmount(deletedAccountInfo.calculatedBalance)}</strong>.
                  You can change the Opening Balance below to start with a different amount.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Account Type */}
        <div>
          <label className="label">Account Type</label>
          <MobileSelect
            value={formData.type}
            onChange={(value) => handleChange({ target: { name: 'type', value } })}
            options={ACCOUNT_TYPES}
            placeholder="Select type"
            label="Account Type"
            columns={3}
            size="sm"
            showIconInTrigger
          />
        </div>

        {/* Balance */}
        <div>
          <label className="label">{isEditing ? 'Balance' : 'Opening Balance'}</label>
          <CalculatorInput
            name="balance"
            value={formData.balance}
            onChange={handleChange}
            placeholder="0.00"
            className="input"
            icon={IndianRupee}
          />
          <p className="text-surface-500 text-xs mt-1">
            {isEditing
              ? 'Current balance (changing this will create an adjustment transaction)'
              : 'Initial balance to start tracking from (creates an Opening Balance transaction)'}
          </p>
        </div>

        {/* Icon & Color - side by side on larger screens */}
        <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
          {/* Icon Picker */}
          <div>
            <label className="label">Icon</label>
            <IconPicker
              value={formData.icon}
              onChange={(icon) => setFormData((prev) => ({ ...prev, icon }))}
              onColorChange={(color) => setFormData((prev) => ({ ...prev, color }))}
              color={formData.color}
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="label">Color</label>
            <ColorPicker
              value={formData.color}
              onChange={(color) => setFormData((prev) => ({ ...prev, color }))}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="label">Description (Optional)</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={2}
            placeholder="Additional notes about this account..."
            className={`input resize-none ${validationErrors.description ? 'input-error' : ''}`}
          />
          {validationErrors.description && (
            <p className="text-expense-600 text-sm mt-1">
              {validationErrors.description}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-surface-200">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary flex-1"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 btn-primary"
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : isEditing
              ? 'Update'
              : 'Add'}
          </button>
        </div>
      </form>

      {/* Balance Reset Confirmation Modal */}
      <ConfirmModal
        isOpen={balanceResetConfirm.open}
        onClose={() => setBalanceResetConfirm({ open: false, newBalance: 0, currentBalance: 0 })}
        onConfirm={handleBalanceResetConfirm}
        title="Reset Account Balance"
        message={
          <div className="space-y-3">
            <p>You are about to reset the account balance. This will create an adjustment transaction.</p>
            <div className="bg-surface-100 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">Current Balance:</span>
                <span className="font-medium">{formatAmount(balanceResetConfirm.currentBalance || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">Adjustment:</span>
                <span
                  className="font-medium"
                  style={{ color: (balanceResetConfirm.newBalance - balanceResetConfirm.currentBalance) >= 0 ? config.incomeColor : config.expenseColor }}
                >
                  {formatAmount(Math.abs(balanceResetConfirm.newBalance - balanceResetConfirm.currentBalance))}
                </span>
              </div>
              <div className="flex justify-between text-sm border-t border-surface-200 pt-1 mt-1">
                <span className="text-surface-600">Final Balance:</span>
                <span className="font-semibold">{formatAmount(balanceResetConfirm.newBalance || 0)}</span>
              </div>
            </div>
            <p className="text-sm text-surface-500">Are you sure you want to proceed?</p>
          </div>
        }
        confirmText="Reset Balance"
        variant="warning"
      />
    </Modal>
  );
}
